import { createPieChartConfigs, type PieChartConfig } from "../../options/pie-chart.options";
import * as d3 from 'd3';
import { numberFormatters } from "../../../core/formatters/index";
import { NumberType } from "../../../core/formatters/number.formatter";
import { COLORS } from "../../options/historical-charts/COLORS";
import './pie-chart.style.scss';
import { capitalized } from "../../../core/formatters/index";
import type { AuthService } from "../../../auth/services/auth.service";
import type { PieChartService } from "../../services/pie-chart.service";

interface PieData {
    label: string;
    value: number;
    percentage: number;
}

export class PieChartComponent extends HTMLElement {
    private _pieData: { [key: string]: number };
    private svg: d3.Selection<SVGSVGElement, unknown, null, undefined> | undefined;
    private width: number = 600;
    private height: number = 300;
    private margin = { top: 20, right: 20, bottom: 20, left: 20 };
    private radius: number = 0;
    private resizeDebounceTimeout: number | null = null;
    private chartId: string | null = null;
    private config?: PieChartConfig;
    private resizeObserver: ResizeObserver | null = null;
    private tooltip: HTMLDivElement | null = null;

    constructor(
        private pieChartService: PieChartService = window.tbwServices.pieChartService,
        private authService: AuthService = window.tbwServices.authService
    ) {
        super();
        this._pieData = {};
        this.classList.add('tbw-pie-chart');
    }

    connectedCallback() {
        this.chartId = this.getAttribute('chart-id');
        if (!this.chartId) { throw 'No chart-id provided'; }

        const config = this.getPieChartConfig(this.chartId);
        if (!config) { throw `No configuration found for chart-id: ${this.chartId}`; }

        this.config = config; 
        this.initializeTemplate();
        this.initializeChart();
        this.setupEventListeners();

        // Add ResizeObserver for container
        const container = this.querySelector('.pie-chart-container');
        if (container) {
            this.resizeObserver = new ResizeObserver(() => {
                this.updateDimensions();
                this.updateChart();
            });
            this.resizeObserver.observe(container);
        }

        // Wait for the next tick to ensure CTA component is properly initialized
        setTimeout(() => {
            // Check if user is already authenticated
            if (this.authService.isAuthenticated) {
                this.loadData();
            }
        }, 0);
    }

    getPieChartConfig(chartId:string): PieChartConfig | undefined {
        return createPieChartConfigs(this.pieChartService).get(chartId);
    }

    disconnectedCallback() {
        this.cleanupEventListeners();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    private initializeTemplate() {
        this.innerHTML = `
            <div class="pie-chart-wrapper" style="position:relative;">
                <div class="pie-chart-content">
                    <div class="pie-chart-container"></div>
                </div>
                <div class="pie-chart-tooltip" style="display:none; position:absolute; z-index:10;"></div>
            </div>
        `;
        const chartContent = this.querySelector('.pie-chart-content');

        const legend = document.createElement('div');
        legend.className = 'pie-chart-legend'

        // Get tooltip element
        const wrapper = this.querySelector('.pie-chart-wrapper');
        this.tooltip = wrapper?.querySelector('.pie-chart-tooltip') as HTMLDivElement;

        if (!chartContent) { return; }
        // console.log('width: ', this.width)
        this.width >= 400 ? chartContent.appendChild(legend) : ""
    }

    private setupEventListeners() {
        window.addEventListener('authChange', this.onAuthChanged.bind(this));
        window.addEventListener('resize', this.onResize.bind(this));
    }

    private onAuthChanged = (e: Event) => {
        const customEvent = e as CustomEvent;
        if (customEvent.detail?.authenticated) {
            this.loadData();
        }
    }

    private cleanupEventListeners() {
        window.removeEventListener('resize', this.onResize.bind(this));
        if (this.resizeDebounceTimeout) {
            window.clearTimeout(this.resizeDebounceTimeout);
        }
    }

    private onResize = () => {
        if (this.resizeDebounceTimeout) {
            window.clearTimeout(this.resizeDebounceTimeout);
        }
        this.resizeDebounceTimeout = window.setTimeout(() => {
            this.updateDimensions();
            this.updateChart();
        }, 250);
    }

    private updateDimensions() {
        const container = this.querySelector('.pie-chart-container') as HTMLElement;
        if (container) {
            const containerRect = container.getBoundingClientRect();
            this.width = containerRect.width;
            this.height = containerRect.height;
            this.radius = Math.min(this.width, this.height) / 2 - Math.max(this.margin.top, this.margin.right, this.margin.bottom, this.margin.left);
        }
    }

    private initializeChart() {
        const container = this.querySelector('.pie-chart-container') as HTMLElement;
        this.updateDimensions();
        this.svg = d3.select(container)
            .append('svg')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('viewBox', [0, 0, this.width, this.height])
            .attr('preserveAspectRatio', 'xMidYMid meet');
    }

    private async loadData() {
        try {
            const rawData = await this.config?.dataLoader();
            this._pieData = this.config?.transformer(rawData);

            if (Object.keys(this._pieData).length === 0) {
                return;
            }

            this.updateChart();
        } catch (error) {
            console.error('Error loading pie chart data:', error);
        }
    }

    private createPieData(): PieData[] {
        const totalValue = Object.values(this._pieData).reduce((sum, value) => sum + value, 0);

        let data = Object.entries(this._pieData)
            .map(([label, value]) => ({
                label,
                value,
                percentage: (value / totalValue) * 100
            }));

        // Sort by value if configured
        if (this.config?.options?.sortByValue) {
            data = data.sort((a, b) => b.value - a.value);
        }

        return data;
    }

    private updateLegend(data: PieData[], colorScale: d3.ScaleOrdinal<string, string>) {
        const legendContainer = this.querySelector('.pie-chart-legend') as HTMLElement;
        if (!legendContainer) return;

        legendContainer.innerHTML = '';

        data.forEach(item => {
            legendContainer.innerHTML += `
            <div class="legend-item">
                <div class="legend-item-content">
                <div class="color-box" style="background-color: ${colorScale(item.label)};"></div>
                    <span class="legend-label">${capitalized(item.label)}</span>
                </div>
                <div class="legend-item-value">
                    <span class="legend-percentage">${item.percentage.toFixed(1)}%</span>
                    ${this.config?.options?.showAbsoluteValue ? `<span class="legend-value">${numberFormatters.defaultFormat(item.value, NumberType.price)}</span>` : ''}
                </div>
            </div>
            `
        });
    }

    private createScales(data: PieData[]) {
        // Dynamically select colors based on the number of data series
        const colors = this.getColorsForData(data.length);
        const colorScale = d3.scaleOrdinal()
            .domain(data.map(d => d.label))
            .range(colors);

        return { colorScale };
    }

    private getColorsForData(seriesCount: number): string[] {
        // If custom colors are specified in config, use them
        if (this.config?.options?.colors) {
            return this.config.options.colors;
        }

        // Otherwise, dynamically select colors from the design system
        const availableColors = COLORS;
        const selectedColors: string[] = [];

        // Evenly distribute colors across the available palette
        for (let i = 0; i < seriesCount; i++) {
            const colorIndex = i % availableColors.length;
            selectedColors.push(availableColors[colorIndex]);
        }

        return selectedColors;
    }

    private updateChart() {
        this.updateDimensions();
        if (!this.svg) return;
        this.svg.attr('viewBox', [0, 0, this.width, this.height]);
        this.svg.selectAll('*').remove();
        const chartGroup = this.svg.append('g')
            .attr('transform', `translate(${this.width / 2},${this.height / 2})`);
        const data = this.createPieData();
        const { colorScale } = this.createScales(data);

        // Update legend
        this.updateLegend(data, colorScale as d3.ScaleOrdinal<string, string>);

        // Tooltip logic
        const tooltip = this.tooltip;
        const wrapper = this.querySelector('.pie-chart-wrapper') as HTMLElement;
        // Get configuration options
        const innerRadius = this.config?.options?.innerRadius || 0;
        const outerRadius = this.config?.options?.outerRadius || 1;
        const labelThreshold = this.config?.options?.labelThreshold || 3;
        const showPercentage = this.config?.options?.showPercentage !== false;
        const showAbsoluteValue = this.config?.options?.showAbsoluteValue !== false;
        const absoluteValueFormat: NumberType = this.config?.options?.absoluteValueFormat ?? NumberType.price
        // Create pie generator
        const pie = d3.pie<PieData>()
            .value(d => d.value)
            .sort(null);
        // Create arc generator
        const arc = d3.arc<d3.PieArcDatum<PieData>>()
            .innerRadius(this.radius * innerRadius)
            .outerRadius(this.radius * outerRadius);
        // Draw pie slices
        const slices = chartGroup.selectAll('.slice')
            .data(pie(data))
            .join('g')
            .attr('class', 'slice');
        slices.append('path')
            .attr('d', arc)
            .attr('fill', d => colorScale(d.data.label) as string)
            .attr('stroke', 'white')
            .style('stroke-width', '2px')
            .style('cursor', 'pointer')
            .on('mouseover', function (_event, d) {
                d3.select(this)
                    .style('opacity', 0.8)
                    .style('stroke-width', '3px');
                if (tooltip) {
                    tooltip.style.display = 'flex';
                    tooltip.innerHTML = `
                      <div style="display:flex;align-items:center;gap:8px;">
                        <div style="width:12px;height:12px;border-radius:3px;background:${colorScale(d.data.label)};"></div>
                        <span style="font-weight:600;">${capitalized(d.data.label)}</span>
                      </div>
                      <div style="margin-top:4px;font-size:13px;">
                        ${d.data.percentage.toFixed(1)}%<br/>
                        ${showAbsoluteValue ? numberFormatters.defaultFormat(d.data.value, NumberType.price) : ''}
                      </div>
                    `;
                }
            })
            .on('mousemove', function (event) {
                if (tooltip && wrapper) {
                    const rect = wrapper.getBoundingClientRect();
                    const mouseX = event.clientX - rect.left;
                    const mouseY = event.clientY - rect.top;
                    tooltip.style.left = (mouseX + 12) + 'px';
                    tooltip.style.top = (mouseY - 24) + 'px';
                }
            })
            .on('mouseout', function (_event, _d) {
                d3.select(this)
                    .style('opacity', 1)
                    .style('stroke-width', '2px');
                if (tooltip) {
                    tooltip.style.display = 'none';
                }
            });
        // Add labels for larger slices
        const labels = slices.filter(d => d.data.percentage > labelThreshold)
            .append('g')
            .attr('class', 'label');
        // Add percentage label
        if (showPercentage) {
            labels.append('text')
                .attr('transform', d => `translate(${arc.centroid(d)})`)
                .attr('dy', '-0.2em')
                .style('text-anchor', 'middle')
                .style('font-size', '12px')
                .style('font-weight', 'bold')
                .style('fill', 'white')
                .style('pointer-events', 'none')
                .text(d => `${d.data.percentage.toFixed(1)}%`);
        }
        // Add absolute value label under percentage
        if (showAbsoluteValue) {
            labels.append('text')
                .attr('transform', d => `translate(${arc.centroid(d)})`)
                .attr('dy', '1em')
                .style('text-anchor', 'middle')
                .style('font-size', '10px')
                .style('font-weight', 'normal')
                .style('fill', 'white')
                .style('pointer-events', 'none')
                .text(d => numberFormatters.defaultFormat(d.data.value, absoluteValueFormat));
        }
    }
} 